import { useContext, useRef } from "react";
import { ThemeContext } from "../../../themes/Theme";
import styles from "./AppetiteIcon.module.css";
import React from "react";
import { useClientId } from "../../../ClientConfig";
import * as client from "../../../client";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { perClient } from "../../../client-features";
import {
  numericAppetiteScore,
  readableAppetiteLong,
  readableAppetiteShort,
} from "../dashboard/util";
import { OverlayPanel } from "primereact/overlaypanel";
import { Button } from "primereact/button";
import { ROUTES } from "../../../routes";
import { GoogleGroupChip } from "../../../components/google-group/GoogleGroupIcon";
import { formatAbbreviatedMoney } from "../Money";

type AppetiteIconProps = {
  naicsInfo: client.NaicsInfo;
  precomputedMetadata: client.PrecomputedMetadata;
  // The report's own broker team inbox, compared against each matching
  // submission's team to mark overlaps from the same team.
  googleGroupEmail: string | null;
  // The report's creation time, which splits matches into prior and later
  // submissions. Server format: UTC without a zone suffix.
  createdAtTs: string;
  status: client.status;
  size?: "small" | "large" | "full_width";
  fontSize?: string;
  justifyContent?: string;
};

const colorForAttributes = (
  appetite: number,
  isLightMode: boolean,
  isDeclinedStatus: boolean
) => {
  if (isDeclinedStatus) {
    return isLightMode ? "var(--gray-100)" : "#11182744";
  }

  switch (appetite) {
    case 5:
      return isLightMode ? "var(--green-100)" : "#0e4f2644";
    case 4:
      return isLightMode ? "var(--cyan-100)" : "#18346244";
    case 3:
      return isLightMode ? "var(--yellow-100)" : "#5e480344";
    case 2:
      return isLightMode ? "var(--orange-100)" : "#642e0944";
    case 1:
      return isLightMode ? "var(--red-100)" : "#66181444";
  }
};

const borderColorForAttributes = (
  appetite: number,
  isLightMode: boolean,
  isDeclinedStatus: boolean
) => {
  if (isDeclinedStatus) {
    return isLightMode ? "var(--gray-400)" : "var(--gray-700)";
  }

  let value = "";
  switch (appetite) {
    case 5:
      value = isLightMode ? "var(--green-400)" : "var(--green-700)";
      break;
    case 4:
      value = isLightMode ? "var(--cyan-400)" : "var(--cyan-700)";
      break;
    case 3:
      value = isLightMode ? "var(--yellow-400)" : "var(--yellow-700)";
      break;
    case 2:
      value = isLightMode ? "var(--orange-400)" : "var(--orange-700)";
      break;
    case 1:
      value = isLightMode ? "var(--red-400)" : "var(--red-700)";
      break;
  }

  return value;
};

const MAX_ADDRESSES_SHOWN = 3;

const SHORT_DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

// created_at_ts is stored in UTC without a zone suffix.
const formatCreatedAt = (createdAtTs: string): string =>
  SHORT_DATE.format(new Date(createdAtTs + "Z"));

// effective_date is a calendar date serialized as a naive midnight timestamp.
const formatEffectiveDate = (effectiveDate: string): string =>
  SHORT_DATE.format(new Date(effectiveDate));

const overlapPct = (
  reportInfo: client.MatchingReportInfo,
  currentTotalTiv: number
): number | null => {
  if (reportInfo.matching_tiv == null || reportInfo.total_tiv == null) {
    return null;
  }
  const smallerTiv = Math.min(currentTotalTiv, reportInfo.total_tiv);
  if (smallerTiv <= 0) {
    return null;
  }
  return (reportInfo.matching_tiv / smallerTiv) * 100;
};

const sameTeam = (a: string | null | undefined, b: string | null): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

type MatchingSubmissionRowProps = {
  reportInfo: client.MatchingReportInfo;
  currentTotalTiv: number;
  currentGoogleGroupEmail: string | null;
  currentCreatedAtTs: string;
};

const MatchingSubmissionRow: React.FC<MatchingSubmissionRowProps> = ({
  reportInfo,
  currentTotalTiv,
  currentGoogleGroupEmail,
  currentCreatedAtTs,
}) => {
  const pct = overlapPct(reportInfo, currentTotalTiv);
  const isPrior = reportInfo.created_at_ts < currentCreatedAtTs;
  const isSameTeam = sameTeam(
    reportInfo.google_group_email,
    currentGoogleGroupEmail
  );
  const addressCount = reportInfo.matching_addresses.length;
  const hiddenAddressCount = Math.max(0, addressCount - MAX_ADDRESSES_SHOWN);
  const result = reportInfo.prior_submission_result ?? null;
  const resultTag =
    result === "prior_submission"
      ? { className: styles.matchTagPriorSubmission, text: "Prior submission" }
      : result === "updated_submission"
        ? {
            className: styles.matchTagUpdatedSubmission,
            text: "Updated submission",
          }
        : {
            className: styles.matchTagMuted,
            text: isPrior ? "Prior" : "Later",
          };

  return (
    <div className={styles.matchRow}>
      <div className={styles.matchTitleRow}>
        <span className={styles.matchName} title={reportInfo.report_name}>
          {reportInfo.report_name || "Unnamed submission"}
        </span>
        {pct != null && (
          <span className={styles.matchOverlap}>{pct.toFixed(1)}%</span>
        )}
        <Button
          link
          className={styles.matchOpenButton}
          icon="pi pi-external-link"
          aria-label="Open submission"
          onClick={() =>
            window.open(
              "#" + ROUTES.DASHBOARD_REPORT(reportInfo.report_id, "review"),
              "_blank"
            )
          }
        />
      </div>
      <div className={styles.matchTagRow}>
        <span className={resultTag.className}>{resultTag.text}</span>
        {reportInfo.google_group_email ? (
          <GoogleGroupChip groupName={reportInfo.google_group_email} />
        ) : (
          <span className={styles.matchTagMuted}>No team</span>
        )}
        {isSameTeam && result !== "updated_submission" && (
          <span className={styles.matchTagMuted}>Same team</span>
        )}
      </div>
      <div className={styles.matchMetaRow}>
        <span>
          <span className={styles.matchMetaLabel}>Eff</span>{" "}
          {reportInfo.effective_date
            ? formatEffectiveDate(reportInfo.effective_date)
            : "—"}
        </span>
        <span className={styles.matchMetaDot}>·</span>
        <span>
          <span className={styles.matchMetaLabel}>Rec'd</span>{" "}
          {formatCreatedAt(reportInfo.created_at_ts)}
        </span>
        {reportInfo.total_tiv != null && reportInfo.total_tiv > 0 && (
          <>
            <span className={styles.matchMetaDot}>·</span>
            <span className={styles.matchMetaTiv}>
              {formatAbbreviatedMoney(reportInfo.total_tiv)}
            </span>
          </>
        )}
      </div>
      <div className={styles.matchAddresses}>
        <div className={styles.matchAddressCount}>
          {addressCount} matching {addressCount === 1 ? "address" : "addresses"}
        </div>
        {reportInfo.matching_addresses
          .slice(0, MAX_ADDRESSES_SHOWN)
          .map((address) => (
            <div key={address} className={styles.matchAddress} title={address}>
              {address}
            </div>
          ))}
        {hiddenAddressCount > 0 && (
          <div className={styles.matchAddressMore}>
            +{hiddenAddressCount} more
          </div>
        )}
      </div>
    </div>
  );
};

const AppetiteIconLuminor: React.FC<AppetiteIconProps> = ({
  size = "small",
  fontSize = "12px",
  justifyContent = "center",
  precomputedMetadata,
  googleGroupEmail,
  createdAtTs,
  status,
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const overlayPanel = useRef<OverlayPanel>(null);

  if (!precomputedMetadata.appetite) {
    return <></>;
  }

  const matchingReports = [
    ...(precomputedMetadata.matching_reports_by_addresses
      ?.matching_report_info ?? []),
  ].sort(
    (a, b) =>
      (overlapPct(b, precomputedMetadata.total_tiv) ?? -1) -
      (overlapPct(a, precomputedMetadata.total_tiv) ?? -1)
  );
  const hasMatchingReport = matchingReports.length > 0;

  const appetiteResults: client.appetite_result[] = precomputedMetadata.appetite
    .appetite_results ?? [precomputedMetadata.appetite.appetite_result];

  const foreignExposurePct = precomputedMetadata.foreign_exposure_pct;

  let tooltipText = appetiteResults
    .filter((r): r is client.appetite_result => r !== "in_appetite")
    .map((r) => {
      if (r === "foreign_exposure" && foreignExposurePct != null) {
        return `More than 20% (${Math.round(
          foreignExposurePct
        )}%) of the schedule's TIV is foreign exposure (outside the 50 US states + DC).`;
      }
      return readableAppetiteLong(r);
    })
    .filter((t): t is string => !!t)
    .join("\n\n");

  if (hasMatchingReport) {
    if (tooltipText) {
      tooltipText += "\n\n";
    }
    tooltipText +=
      "This submission has a location overlap with another submission. Click the top right badge to learn more.";
  }

  const shouldShowTooltip =
    precomputedMetadata.appetite.appetite_result !== "in_appetite" ||
    hasMatchingReport;

  return (
    <TooltipWrapper
      tooltipText={tooltipText}
      tooltipVisible={shouldShowTooltip}
    >
      <div
        className={styles.iconContainer}
        style={{
          width:
            size === "small" ? "132px" : size === "large" ? "170px" : "100%",
          height: size === "small" ? "22px" : "32px",
          backgroundColor: colorForAttributes(
            numericAppetiteScore(precomputedMetadata.appetite.appetite_result),
            isLightMode,
            false
          ),
          borderColor: borderColorForAttributes(
            numericAppetiteScore(precomputedMetadata.appetite.appetite_result),
            isLightMode,
            false
          ),
          borderRadius: size === "small" ? "4px" : "6px",
          fontSize: fontSize,
          justifyContent: justifyContent,
        }}
      >
        {hasMatchingReport && (
          <>
            <Button
              raised
              className={styles.badgeButton}
              onClick={(e) => {
                if (overlayPanel.current) {
                  overlayPanel.current.toggle(e);
                }
              }}
              label={`${matchingReports.length}`}
              severity={
                status === "declined" || status === "closed"
                  ? "secondary"
                  : "info"
              }
            />
            <OverlayPanel
              ref={overlayPanel}
              className={styles.matchingOverlay}
              showCloseIcon
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.matchingHeader}>
                <span className={styles.matchingTitle}>
                  Matching Submissions
                </span>
                <span className={styles.matchingCount}>
                  {matchingReports.length}
                </span>
              </div>
              <div className={styles.matchingSubtitle}>
                Overlap is the shared TIV as a share of the smaller schedule.
              </div>
              <div className={styles.matchingList}>
                {matchingReports.map((reportInfo) => (
                  <MatchingSubmissionRow
                    key={reportInfo.report_id}
                    reportInfo={reportInfo}
                    currentTotalTiv={precomputedMetadata.total_tiv}
                    currentGoogleGroupEmail={googleGroupEmail}
                    currentCreatedAtTs={createdAtTs}
                  />
                ))}
              </div>
            </OverlayPanel>
          </>
        )}
        <span className={styles.text}>
          {readableAppetiteShort(precomputedMetadata.appetite.appetite_result)}
        </span>
      </div>
    </TooltipWrapper>
  );
};

export const AppetiteIcon: React.FC<AppetiteIconProps> = ({
  naicsInfo,
  precomputedMetadata,
  googleGroupEmail,
  createdAtTs,
  status,
  size = "small",
  fontSize = "12px",
  justifyContent = "center",
}) => {
  const clientId = useClientId();
  return perClient({
    luminor: () => (
      <AppetiteIconLuminor
        naicsInfo={naicsInfo}
        status={status}
        precomputedMetadata={precomputedMetadata}
        googleGroupEmail={googleGroupEmail}
        createdAtTs={createdAtTs}
        size={size}
        fontSize={fontSize}
        justifyContent={justifyContent}
      />
    ),
  })[clientId]?.();
};
